import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useMediaQuery } from '@monofront/business/hooks';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';
import { trackEvent } from '@monofront/tracking';
import { IProduct, IProductLayoutSection } from '@monofront/types';
import { LayoutContext } from '@context/LayoutContext';
import ProductPriceActions from '../ProductPriceActions';
import { isInViewport } from '@utils/view';

interface IMenuItem {
  id: string;
  name: string;
}

interface IProductStickyMenuProps {
  sections: IProductLayoutSection[];
  product: IProduct;
}

const eventSectionMapping = {
  'section-presentation': 'about',
  'section-cocreation': 'co-creation',
  'section-advices': 'tips',
  'section-vendor': 'vendor',
  'section-reviews': 'reviews',
};

const ProductStickyMenu: React.FC<IProductStickyMenuProps> = ({
  sections,
  product,
}) => {
  const stickyNavRef = useRef<HTMLElement>(null);
  const [menuItems, setMenuItems] = useState<IMenuItem[] | null>(null);
  const [stickyHeight, setStickyHeight] = useState(100);
  const [isAddToCartCtaVisible, setIsAddToCartCtaVisible] = useState(true);
  const isMobile = useMediaQuery(800);
  const { mainHeaderHeight } = useContext(LayoutContext);
  const { formatProductWeight } = useFormatProductWeight();
  const formattedProductWeight = formatProductWeight(
    product.meta.finance,
    false,
  );

  const updateStickyHeight = useCallback(() => {
    if (!stickyNavRef?.current) return;

    setStickyHeight(stickyNavRef.current.offsetHeight);
  }, []);

  const goToAnchor = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    const anchorId = e.currentTarget.getAttribute('href') || '';
    const anchorElement = document.querySelector(anchorId);

    if (!anchorElement) return;

    anchorElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    document
      ?.getElementById(anchorId.replace('#', ''))
      ?.focus({ preventScroll: true });

    trackEvent({
      type: 'LFProductAnchorBannerButtonUsed',
      data: {
        productAnchorBannerButton:
          eventSectionMapping[anchorId.replace('#', '')] || 'unknown',
      },
    });
  };

  useEffect(() => {
    // set menu items
    const items = sections.reduce<IMenuItem[]>((sectionList, section) => {
      const id = section.ref.current?.id;
      const name = section.ref.current?.getAttribute('data-menu');

      if (id && name) {
        return [
          ...sectionList,
          {
            id,
            name,
          },
        ];
      }

      return sectionList;
    }, []);

    setMenuItems(items);
  }, []);

  useEffect(() => {
    // detect when menu is stuck https://troll-winner.com/blog/css-position-sticky-how-to-detect-element-is-stuck/
    if (!menuItems?.length) return;

    const updateStickyMenu = () => {
      if (!stickyNavRef.current) return;

      const parentTop =
        stickyNavRef.current.parentElement?.getBoundingClientRect().top || 0;
      const top = Math.round(
        stickyNavRef.current.getBoundingClientRect().top - parentTop,
      );

      stickyNavRef.current.style.position = 'static';
      stickyNavRef.current.toggleAttribute(
        'data-stuck',
        top !==
          Math.round(
            stickyNavRef.current.getBoundingClientRect().top - parentTop,
          ),
      );
      stickyNavRef.current.style.position = '';
      stickyNavRef.current.dataset.prevClientTop = String(top);
    };

    updateStickyMenu();
    updateStickyHeight();

    const handleScroll = () => {
      if (!stickyNavRef.current) return;

      const parentTop =
        stickyNavRef.current.parentElement?.getBoundingClientRect().top || 0;
      const top = Math.round(
        stickyNavRef.current.getBoundingClientRect().top - parentTop,
      );

      stickyNavRef.current.toggleAttribute(
        'data-stuck',
        Number(stickyNavRef.current.dataset.prevClientTop) !== top,
      );
      stickyNavRef.current.dataset.prevClientTop = String(top);

      setIsAddToCartCtaVisible(
        isInViewport(-70, document.querySelector('#main-price-actions')),
      );
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateStickyHeight);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateStickyHeight);
    };
  }, [menuItems]);

  if (!menuItems || menuItems.length === 0) {
    return null;
  }

  return (
    <nav
      ref={stickyNavRef}
      className={`tw-sticky tw-z-20 tw-mt-12 tw-bg-white tw-py-2
      max-[800px]:tw-lf-layout-container-full
      desktop:tw-fixed desktop:tw-left-0
      desktop:tw-w-full
      desktop:tw-border desktop:tw-border-0
      desktop:tw-border-t desktop:tw-border-solid desktop:tw-border-blue-200
      desktop:tw-px-6 desktop:tw-py-3
      desktop:tw-transition desktop:tw-duration-300 desktop:tw-ease-in-out
      ${!isAddToCartCtaVisible ? ' desktop:-tw-translate-y-full' : ''}
    `}
      style={
        isMobile
          ? { top: `${mainHeaderHeight}px` }
          : { bottom: `-${stickyHeight}px` }
      }
    >
      <div className="desktop:tw-lf-layout-container desktop:tw-flex desktop:tw-items-center">
        {!isMobile && product.images?.[0] && (
          <div className="tw-shrink-0 [&>span]:tw-align-top">
            <ResponsiveImage
              src={product.images[0]}
              layout="fixed"
              width={64}
              height={64}
              UNSAFE_className="tw-rounded-lg"
            />
          </div>
        )}
        <div className="tw-grow desktop:tw-pl-3 desktop:tw-pr-2">
          {!isMobile && (
            <h2 className="tw-lf-body-bold-md tw-hidden desktop:tw-block">
              {product.title}
              {formattedProductWeight && ` ${formattedProductWeight}`}
            </h2>
          )}
          <ul
            id="product-menu"
            className={`tw-overflow-x-auto tw-whitespace-nowrap tw-pl-2 desktop:tw-whitespace-normal desktop:tw-pl-0`}
          >
            {menuItems.map((menuItem, index) => (
              <li
                key={index}
                id={`linkto-${menuItem.id}`}
                className={`
                tw-inline-block
                desktop:tw-mt-2
              `}
              >
                <a
                  href={`#${menuItem.id}`}
                  onClick={goToAnchor}
                  className={`
                  tw-lf-chip tw-lf-chip-suggestion tw-lf-chip-sm tw-mr-2
                `}
                >
                  {menuItem.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
        {!isMobile && (
          <ProductPriceActions
            product={product}
            UNSAFE_className={`
            !tw-p-0 !tw-m-0 !tw-border-0
            tw-gap-4
            [&_.product-price]:!tw-flex
            [&_.product-price]:!tw-items-center
            [&_.product-price]:!tw-gap-2
            [&_.product-price>span]:!tw-m-0
            [&_.product-price-striked]:!tw-order-2
            tw-shrink-0
          `}
          />
        )}

        <style jsx>{`
          ul {
            scrollbar-width: none;
          }

          ul::-webkit-scrollbar {
            display: none;
          }

          :global(.product-details .section-title) {
            scroll-margin-top: ${mainHeaderHeight && isMobile
              ? `${mainHeaderHeight + stickyHeight + 20}px`
              : mainHeaderHeight
              ? `${mainHeaderHeight + 20}px`
              : '250px'};
          }
        `}</style>
      </div>
    </nav>
  );
};

export default ProductStickyMenu;
